// src/hooks/useKioskInactivity.js
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKioskDraft } from '../context/KioskDraftContext';

/**
 * Custom hook for tracking user inactivity on kiosk screens
 * Redirects to /kiosk home after specified timeout (default: 2 minutes)
 *
 * @param {number} timeout - Inactivity timeout in milliseconds (default: 120000ms / 2 minutes)
 * @param {boolean} enabled - Whether the inactivity timer is enabled (default: true)
 */
const useKioskInactivity = (timeout = 120000, enabled = true) => {
  const navigate = useNavigate();
  const { clearDraft } = useKioskDraft();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const resetTimer = () => {
      // Clear existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set new timer
      // replace: Back must not reopen the previous customer's screen
      timerRef.current = setTimeout(() => {
        clearDraft(); // the next person must not see this customer's details
        navigate('/kiosk', { replace: true });
      }, timeout);
    };

    // Set initial timer
    resetTimer();

    // Activity events that reset the timer
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [timeout, enabled, navigate, clearDraft]);
};

export default useKioskInactivity;
